import { useState, useEffect, ReactElement } from "react";
import { Navigate } from "react-router-dom";
import CurrentSession from "data/userSession";
import authInstance from "data/firestore/auth";
import VerifyEmail from "features/layout/VerifyEmail";
import { useCustomSnackbar } from "hooks/customSnackbar";

interface AuthGuardProps {
  children: ReactElement;
}

const AuthGuard = ({ children }: AuthGuardProps) => {
  const { enqueueSnackbar } = useCustomSnackbar();
  const [isSignedIn, setIsSignedIn] = useState<boolean | null>(null);
  // Default false so we never flash the verify screen while auth is resolving.
  const [needsVerification, setNeedsVerification] = useState(false);

  useEffect(() => {
    let active = true;
    const check = () =>
      CurrentSession.load().then(() => {
        if (!active) return;
        setIsSignedIn(CurrentSession.isSignedIn);
        setNeedsVerification(authInstance.needsEmailVerification);
      });
    check();
    // Re-check on sign-in/out so a user who logs out while on a protected page is
    // redirected to /login instead of staying put and firing unauthenticated reads.
    window.addEventListener("authStateChanged", check);
    return () => {
      active = false;
      window.removeEventListener("authStateChanged", check);
    };
  }, []);

  // Once we know a signed-out user landed on a protected page, tell them why
  // they're being bounced to login instead of a silent login-page flash.
  // preventDuplicate stops rapid tab taps from stacking toasts.
  useEffect(() => {
    if (isSignedIn === false) {
      enqueueSnackbar("Please log in to access that page.", {
        variant: "warning",
        preventDuplicate: true,
      });
    }
  }, [isSignedIn, enqueueSnackbar]);

  // Still resolving auth state -- render nothing so already-signed-in users never
  // flash the login page before we know they're authenticated.
  if (isSignedIn === null) return null;

  if (!isSignedIn) return <Navigate to="/login" replace />;

  // Pure password accounts that haven't verified -- hold at the verify screen.
  // Anyone with a social provider (Google/Apple) is let through.
  if (needsVerification) return <VerifyEmail />;

  return children;
};

export default AuthGuard;
